import type { SubscriptionPlan, SubscriptionState, PlanDetails, ClerkProduct } from '../types/subscription';

const SUBSCRIPTION_CACHE_KEY = 'tradevision_subscription_cache_v3'; // v3: fixed auto-subscribe cache invalidation
const PLANS_CACHE_KEY = 'tradevision_plans_cache';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const PLANS_CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

interface CachedSubscription {
  plan: SubscriptionPlan;
  expiresAt?: string;
  cancelAtPeriodEnd?: boolean;
  cachedAt: number;
}

interface CachedPlans {
  plans: PlanDetails[];
  cachedAt: number;
}

export const subscriptionService = {
  /**
   * Get subscription from Clerk user metadata
   * Clerk stores subscription info in publicMetadata or privateMetadata
   */
  async getSubscriptionFromClerk(user: any): Promise<SubscriptionState> {
    if (!user) {
      console.log('[subscriptionService] No user provided, returning default state');
      return this.getDefaultState();
    }

    try {
      // Get subscription from Clerk user metadata first to check for updates
      const metadata = user.publicMetadata || {};
      const subscriptionStatus = metadata.subscriptionStatus as string;
      const subscriptionPlanFromMetadata = metadata.subscriptionPlan as string;
      
      // Check cache
      const cached = this.getCachedSubscription();
      const cacheValid = cached && cached.cachedAt + CACHE_DURATION_MS > Date.now();
      
      // If cache says 'free' but Clerk metadata says user has active subscription,
      // ignore cache and use Clerk metadata (cache is stale)
      const metadataHasActiveSubscription = subscriptionStatus === 'active' && 
        subscriptionPlanFromMetadata && subscriptionPlanFromMetadata !== 'free';
      
      if (cacheValid && cached.plan !== 'free') {
        // Cache has a paid plan, use it
        console.log('[subscriptionService] Using cached subscription:', cached.plan, 'cancelAtPeriodEnd:', cached.cancelAtPeriodEnd);
        return this.buildState(
          cached.plan, 
          cached.expiresAt ? new Date(cached.expiresAt) : undefined,
          cached.cancelAtPeriodEnd
        );
      } else if (cacheValid && cached.plan === 'free' && !metadataHasActiveSubscription) {
        // Cache says free and metadata agrees, use cache
        console.log('[subscriptionService] Using cached subscription:', cached.plan, 'cancelAtPeriodEnd:', cached.cancelAtPeriodEnd);
        return this.buildState(
          cached.plan, 
          cached.expiresAt ? new Date(cached.expiresAt) : undefined,
          cached.cancelAtPeriodEnd
        );
      } else if (metadataHasActiveSubscription) {
        console.log('[subscriptionService] Cache stale - Clerk metadata shows active subscription:', subscriptionPlanFromMetadata);
      }
      
      // Check for plan tier name first (set by auto-subscribe and webhook)
      const subscriptionPlan = metadata.subscriptionPlan as string;
      const productId = metadata.stripeProductId as string;
      
      // Determine plan tier
      let plan: SubscriptionPlan = 'free';
      
      // Valid plan tier names
      const validPlanTiers = ['free', 'essential', 'plus', 'premium'];
      
      // First check if subscriptionPlan is a valid tier name (preferred)
      if (subscriptionPlan && validPlanTiers.includes(subscriptionPlan.toLowerCase())) {
        plan = subscriptionPlan.toLowerCase() as SubscriptionPlan;
        console.log('[subscriptionService] Using subscriptionPlan tier directly:', plan);
      } else if (productId && productId.startsWith('prod_')) {
        // Product ID format - need to map to plan tier
        try {
          const plans = await this.fetchPlansFromClerk();
          const matchingPlan = plans.find(p => p.productId === productId);
          if (matchingPlan) {
            plan = matchingPlan.id;
            console.log('[subscriptionService] Mapped Product ID to plan tier:', {
              productId,
              planTier: plan,
              planName: matchingPlan.name
            });
          } else {
            // Fallback: any paid product should grant premium access
            console.warn('[subscriptionService] Could not find matching plan for Product ID, defaulting to premium:', productId);
            plan = 'premium';
          }
        } catch (error) {
          console.error('[subscriptionService] Error fetching plans for Product ID mapping, defaulting to premium:', error);
          plan = 'premium'; // Default to premium for paid products
        }
      } else if (subscriptionPlan) {
        // Legacy: subscriptionPlan might be a product ID
        if (subscriptionPlan.startsWith('prod_')) {
          try {
            const plans = await this.fetchPlansFromClerk();
            const matchingPlan = plans.find(p => p.productId === subscriptionPlan);
            if (matchingPlan) {
              plan = matchingPlan.id;
            } else {
              plan = 'premium'; // Default to premium for paid products
            }
          } catch {
            plan = 'premium'; // Default to premium for paid products
          }
        } else {
          plan = subscriptionPlan as SubscriptionPlan;
        }
      }

      const expiresAt = metadata.subscriptionExpiresAt ? new Date(metadata.subscriptionExpiresAt) : undefined;
      const cancelAtPeriodEnd = metadata.cancelAtPeriodEnd === true;
      
      // Check if subscription is actually active in Stripe
      // Note: subscriptionStatus is already declared at the top of this function
      const activeStatuses = ['active', 'trialing'];
      const isSubscriptionActive = activeStatuses.includes(subscriptionStatus);

      console.log('[subscriptionService] Fetched from Clerk metadata:', {
        productId,
        subscriptionPlan: metadata.subscriptionPlan,
        subscriptionStatus,
        isSubscriptionActive,
        cancelAtPeriodEnd,
        resolvedPlan: plan,
        userId: user.id
      });

      // If subscription is not active, treat as free plan
      const effectivePlan = isSubscriptionActive ? plan : 'free';

      // Cache the result including cancelAtPeriodEnd
      this.cacheSubscription(effectivePlan, expiresAt, cancelAtPeriodEnd);

      return this.buildState(effectivePlan, expiresAt, cancelAtPeriodEnd);
    } catch (error) {
      console.error('[subscriptionService] Error getting subscription:', error);
      return this.getDefaultState();
    }
  },

  /**
   * Check if user has access to a specific feature
   */
  hasFeatureAccess(state: SubscriptionState, feature: 'replay' | 'tickReplay' | 'multiChart' | 'advancedAnalysis' | 'api' | 'aiAnalysis'): boolean {
    // If subscription is not active, user only has free tier access
    if (!state.isActive) {
      // Free tier only - check if feature is available on free tier
      const freeFeatures: string[] = []; // No premium features on free tier
      return freeFeatures.includes(feature);
    }

    const planHierarchy: Record<string, number> = {
      free: 0,
      essential: 1,
      plus: 2,
      premium: 3,
    };

    const featureRequirements: Record<string, SubscriptionPlan> = {
      replay: 'essential',
      tickReplay: 'plus',
      multiChart: 'essential',
      advancedAnalysis: 'plus',
      api: 'premium',
      aiAnalysis: 'premium', // AI Guru analysis and chat requires premium
    };

    // Normalize plan - handle product IDs by mapping to tier names
    let normalizedPlan = state.plan;
    if (state.plan && state.plan.startsWith('prod_')) {
      // Product ID - treat as premium (highest tier) since paid products are premium
      // This handles cases where Clerk metadata has product ID instead of tier name
      normalizedPlan = 'premium';
    }

    const requiredPlan = featureRequirements[feature];
    const userPlanLevel = planHierarchy[normalizedPlan] ?? 0;
    const hasAccess = userPlanLevel >= planHierarchy[requiredPlan];
    
    return hasAccess;
  },

  /**
   * Get the Clerk billing portal URL for subscription management
   */
  getBillingPortalUrl(): string {
    // Clerk provides a billing portal through their dashboard
    // This URL should be configured in your Clerk dashboard
    return '/billing';
  },

  /**
   * Fetch available subscription plans from Clerk API via backend
   * Throws error if API fails - no fallback to ensure backend is working
   */
  async fetchPlansFromClerk(): Promise<PlanDetails[]> {
    try {
      // Check cache first
      const cached = this.getCachedPlans();
      if (cached && 
          cached.plans && 
          cached.plans.length > 0 && 
          cached.cachedAt + PLANS_CACHE_DURATION_MS > Date.now()) {
        console.log('[subscriptionService] Using cached plans');
        return cached.plans;
      }

      // Determine API URL
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const endpoint = `${apiUrl}/api/billing/plans`;
      
      console.log('[subscriptionService] Fetching plans from:', endpoint);
      
      // Fetch from backend API
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch plans: ${response.status} ${response.statusText}`);
      }

      const clerkProducts: ClerkProduct[] = await response.json();
      
      if (!Array.isArray(clerkProducts) || clerkProducts.length === 0) {
        throw new Error('Invalid response: No plans returned from API');
      }
      
      const plans = this.transformClerkProductsToPlans(clerkProducts);
      
      // Cache the result
      this.cachePlans(plans);
      
      console.log(`[subscriptionService] Successfully fetched ${plans.length} plans from API`);
      return plans;
    } catch (error) {
      console.error('[subscriptionService] Error fetching plans:', error);
      throw error; // Propagate error instead of falling back
    }
  },

  /**
   * Transform Clerk products to our PlanDetails format
   */
  transformClerkProductsToPlans(products: ClerkProduct[]): PlanDetails[] {
    return products.map(product => {
      const metadata = product.metadata || {};
      const planTier = (metadata.plan_tier || 'free') as SubscriptionPlan;
      
      // Parse features from metadata
      let features = [];
      try {
        features = metadata.features ? JSON.parse(metadata.features) : [];
      } catch {
        features = [];
      }

      // Find monthly and yearly prices from Stripe
      const monthlyPrice = product.prices?.find(p => p.interval === 'month');
      const yearlyPrice = product.prices?.find(p => p.interval === 'year');

      return {
        id: planTier,
        productId: product.id, // Store Stripe Product ID
        name: product.name, // This will be "Gold" if renamed in Stripe
        price: monthlyPrice ? monthlyPrice.unit_amount / 100 : 0,
        priceYearly: yearlyPrice ? yearlyPrice.unit_amount / 100 : undefined,
        interval: 'month',
        description: product.description,
        features: features,
        cta: `Upgrade to ${product.name}`,
        // Use real Stripe price IDs (not Clerk synthetic IDs)
        clerkPriceId: monthlyPrice?.id,
        clerkPriceIdYearly: yearlyPrice?.id,
      };
    });
  },

  /**
   * Get checkout URL for a specific plan
   * Uses backend API to create Clerk checkout session
   */
  async getCheckoutUrl(plan: SubscriptionPlan, billingPeriod: 'monthly' | 'yearly' = 'monthly'): Promise<string> {
    try {
      const plans = await this.fetchPlansFromClerk();
      const selectedPlan = plans.find(p => p.id === plan);
      
      if (!selectedPlan) {
        throw new Error(`Plan not found: ${plan}`);
      }

      const priceId = billingPeriod === 'yearly' 
        ? selectedPlan.clerkPriceIdYearly 
        : selectedPlan.clerkPriceId;

      if (!priceId) {
        throw new Error(`No price ID found for ${plan} (${billingPeriod})`);
      }

      // Use backend API to create checkout session
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      return `${apiUrl}/api/billing/checkout?price_id=${priceId}`;
    } catch (error) {
      console.error('[subscriptionService] Error getting checkout URL:', error);
      throw error;
    }
  },

  buildState(plan: SubscriptionPlan, expiresAt?: Date, cancelAtPeriodEnd?: boolean): SubscriptionState {
    return {
      plan,
      isActive: plan !== 'free',
      expiresAt: expiresAt?.getTime(),
      cancelAtPeriodEnd: cancelAtPeriodEnd || false,
    };
  },

  getDefaultState(): SubscriptionState {
    return {
      plan: 'free',
      isActive: false,
    };
  },

  cachePlans(plans: PlanDetails[]): void {
    try {
      const cached: CachedPlans = {
        plans,
        cachedAt: Date.now(),
      };
      localStorage.setItem(PLANS_CACHE_KEY, JSON.stringify(cached));
    } catch {
      // Ignore storage errors
    }
  },

  getCachedPlans(): CachedPlans | null {
    try {
      const raw = localStorage.getItem(PLANS_CACHE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as CachedPlans;
    } catch {
      return null;
    }
  },

  cacheSubscription(plan: SubscriptionPlan, expiresAt?: Date, cancelAtPeriodEnd?: boolean): void {
    try {
      const cached: CachedSubscription = {
        plan,
        expiresAt: expiresAt?.toISOString(),
        cancelAtPeriodEnd: cancelAtPeriodEnd || false,
        cachedAt: Date.now(),
      };
      localStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify(cached));
    } catch {
      // Ignore storage errors
    }
  },

  getCachedSubscription(): CachedSubscription | null {
    try {
      const raw = localStorage.getItem(SUBSCRIPTION_CACHE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as CachedSubscription;
    } catch {
      return null;
    }
  },

  clearCache(): void {
    try {
      localStorage.removeItem(SUBSCRIPTION_CACHE_KEY);
    } catch {
      // Ignore
    }
  },
};
